import ReorderControls from "../../common/ReorderControls";
// Import package components
import { useContext, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Icon, Image } from "uikit-react";
import UIkit from "uikit";

import { AppContexts } from "../../../providers";
import MasterPropertyGuideAdd, {
  MODAL_NAME as MASTER_PROPERTY_GUIDE_ADD_MODAL_NAME,
} from "./add";
import MasterPropertyGuideEdit, {
  MODAL_NAME as MASTER_PROPERTY_GUIDE_EDIT_MODAL_NAME,
} from "./edit";
import TabAdd from "./add_tab";
import TabNameInput from "./tab-name-input";
import { usePreviewOverflow } from "./use-preview-overflow";
import { useQueuedSave } from "../../guide_designer/use-queued-save";
import IconList from "./icon_list";
import classnames from "classnames";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";
import GuideIcon from "../../common/GuideIcon";
import GuidePhotoPreview from "../../common/GuidePhotoPreview";
import Loading from "../../common/Loading";
import Markdown from "../../common/Markdown";
import FeatherIcon from "../../common/FeatherIcon";
import MasterDelete from "./delete";
import ResetToMaster from "./reset";
import MasterTabDelete from "./delete_tab";
import HomeSection from "./home-section";
import { Link } from "../../../ui";
import { useHistory } from "react-router-dom";
import Share from "../share";
import VisibilityShow from "../../common/VisibilityShow";
import QRPart from "../../common/Qrcode";
import { usePermissionsStore } from "../../permissions/permissions_store";
import {
  getInitialMasterGuideTabType,
  getMasterGuideTabLabel,
  getMasterGuideTabMove,
  getMasterGuideTabOrder,
  removeMasterGuideTab,
} from "./tab_order";
import {
  mergeGuideSectionOverrides,
  mergeSavedGuideSection,
  normalizeOrderedIds,
  removeGuideSection,
  selectOrderedGuideSections,
  upsertGuideSection,
} from "./section_state";
import {
  renameWelcomeTab,
  resolveSavedWelcomeTabs,
} from "./tab_name_state";

const DragHandleTab = SortableHandle(() => (
  <button
    type="button"
    className="uk-button hms-simple-icon-btn uk-margin-small-right master-guide-sort-handle"
    uk-tooltip="Drag to reorder sections"
  >
    <Image src="/images/move.svg" />
  </button>
));

const SortableItemTab = SortableElement(({ tab, no, hasLeadingGap, selectedTab, isMaster, canManageMasterGuide, resolveTabLabel, setSelectedTab, handleEditTab, onRename }) => {
  const isArrivalTab = tab?.type === "arrival";

  return (
  <div className="master-guide-list">
    <div
      key={no}
      data-tab-type={tab?.type || ""}
      className={classnames("master-guide-list-item", {
        "master-active": tab?.type === selectedTab?.type,
        "is-locked": isArrivalTab || !isMaster || !canManageMasterGuide,
        "has-leading-gap": hasLeadingGap,
      })}
      onClick={() => {
        setSelectedTab(tab);
      }}
    >
      {isArrivalTab || !isMaster || !canManageMasterGuide ? (
        <span className="master-guide-list-label" tabIndex="0">
          {resolveTabLabel(tab)}
        </span>
      ) : (
        <TabNameInput tab={tab} onSave={onRename} />
      )}
      {isArrivalTab || !isMaster || !canManageMasterGuide ? null : (
        <div className="master-guide-list-actions">
          <DragHandleTab />
          <span
            onClick={() => handleEditTab(tab)}
            uk-toggle="target: #master-tab-delete"
            uk-tooltip="Delete Tab"
            style={{ cursor: "pointer" }}
          >
            <FeatherIcon name="trash-2" />
          </span>
        </div>
      )}
    </div>
  </div>
  );
});

const SortableListTab = SortableContainer(({ tabs, ...rowProps }) => (
  <div className="master-guide-tab">
    {tabs.map((t, i) => (
      <SortableItemTab
        {...rowProps}
        tab={t}
        index={i}
        no={i}
        key={t?.type}
        hasLeadingGap={i > 0 && tabs[i - 1]?.type === "arrival"}
        disabled={t?.type === "arrival" || !rowProps.canManageMasterGuide}
      />
    ))}
  </div>
));


const MASTER_GUIDE_SELECTED_TAB_KEY = "master-home-guide-selected-tab";

const PropertyGuide = ({
  embedded = false,
  onSaved,
  getTabLabel,
  focusedTabType = "",
  onRegisterSave,
}) => {
  const { canEdit, canView } = usePermissionsStore();
  const { user, refresh } = useContext(AppContexts.AuthContext);
  const {
    rental,
    setRental,
    getRental,
    updateRental,
    updateSection,
    createSection,
    selectedTab,
    setSelectedTab,
    selectedSection,
    setSelectedSection,
  } = useContext(AppContexts.RentalsContext);

  const rental_id = window.location.pathname.split("/")[2];
  const history = useHistory();

  // const MasterGuideContext = useContext(AppContexts.MasterGuideContext);
  const {
    get,
    set,
    setGuestbookTabOrder,
    settings,
    setSettings,
  } = useContext(
    AppContexts.SettingsContext,
  );

  const { getSurveyList } = useContext(AppContexts.SurveysContext);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const nameSaveQueue = useQueuedSave();
  useEffect(() => onRegisterSave?.(nameSaveQueue), [onRegisterSave, nameSaveQueue]);

  const handleRenameTab = (type, label) => nameSaveQueue(async () => {
    const tabs = settingsRef.current.welcome_tabs || [];
    if (!tabs.some(tab => tab.type === type)) throw new Error("Section no longer exists");
    const nextTabs = renameWelcomeTab(tabs, type, label);
    settingsRef.current = { ...settingsRef.current, welcome_tabs: nextTabs };
    setSettings(current => ({ ...current, welcome_tabs: nextTabs }));

    try {
      const saved = await set(["welcome_tabs"], { welcome_tabs: nextTabs });
      const savedTabs = resolveSavedWelcomeTabs(saved, nextTabs);
      settingsRef.current = { ...settingsRef.current, welcome_tabs: savedTabs };
      setSettings(current => ({ ...current, welcome_tabs: savedTabs }));
      setSelectedTab(current => current?.type === type
        ? savedTabs.find(tab => tab.type === type) || current
        : current);
      onSaved?.();
    } catch (error) {
      settingsRef.current = { ...settingsRef.current, welcome_tabs: tabs };
      setSettings(current => ({ ...current, welcome_tabs: tabs }));
      throw error;
    }
  });


  const [loading, setLoading] = useState(true);
  const [isProcessing, setProcessing] = useState(false);
  const [isEditTitle, setisEditTitle] = useState(false);
  const [title, setTitle] = useState("");
  const [newSectionName, setNewSectionName] = useState("");
  const [previewExpandedSections, setPreviewExpandedSections] = useState({});

  const resolveTabLabel = useCallback(
    (tab) => {
      if (typeof getTabLabel === "function") {
        const resolvedLabel = getTabLabel(tab);
        if (resolvedLabel) {
          return resolvedLabel;
        }
      }

      return getMasterGuideTabLabel(tab);
    },
    [getTabLabel],
  );
  const normalizeOrdering = useCallback((ordering = []) => {
    return normalizeOrderedIds(ordering);
  }, []);

  const normalizeWelcomeOrder = useCallback(
    (orders = []) =>
      (orders || []).map((order) => ({
        ...order,
        ordering: normalizeOrdering(order?.ordering || []),
      })),
    [normalizeOrdering],
  );

  const normalizedWelcomeOrder = useMemo(
    () => normalizeWelcomeOrder(settings?.welcome_order || []),
    [normalizeWelcomeOrder, settings?.welcome_order],
  );

  const [mergedWelcomeOrder, setMergedWelcomeOrder] = useState(
    normalizedWelcomeOrder,
  );

  useEffect(() => {
    setMergedWelcomeOrder(normalizedWelcomeOrder);
  }, [normalizedWelcomeOrder]);

  let tabOrder = useMemo(() => {
    return getMasterGuideTabOrder(settings);
  }, [settings]);

  let sectionOrder = useMemo(() => {
    if (!selectedTab || !mergedWelcomeOrder.length) {
      return [];
    }

    return (
      mergedWelcomeOrder.filter((wo) => wo.type === selectedTab.type)[0]
        ?.ordering || []
    );
  }, [mergedWelcomeOrder, selectedTab]);

  let { guestbook, welcome_tabs, welcome_order } = useMemo(() => {
    return settings;
  }, [settings]);

  useEffect(() => {
    if (!settings?.welcome_order) {
      return;
    }

    const existing = JSON.stringify(settings.welcome_order || []);
    const normalized = JSON.stringify(normalizedWelcomeOrder);

    if (existing === normalized) {
      return;
    }

    setSettings((prev) => ({
      ...prev,
      welcome_order: normalizedWelcomeOrder,
    }));
    set("welcome_order", normalizedWelcomeOrder);
  }, [normalizedWelcomeOrder, set, setSettings, settings?.welcome_order]);

  const orderedTabs = useMemo(
    () => {
      const tabs = tabOrder
        .map((orderType) => (welcome_tabs || []).find((t) => orderType === t.type))
        .filter(Boolean);

      const arrivalTab = tabs.find((tab) => tab.type === "arrival");
      const otherTabs = tabs.filter((tab) => tab.type !== "arrival");

      return arrivalTab ? [arrivalTab, ...otherTabs] : otherTabs;
    },
    [tabOrder, welcome_tabs],
  );

  useEffect(() => {
    setTitle(rental.name);
  }, [rental]);

  const sectionScopeKey = rental?.rental_id
    ? String(rental.rental_id)
    : "master";
  const [resetSections, setResetSections] = useState({ scope: sectionScopeKey, ids: [] });
  const serverSections = useMemo(() => {
    if (!rental) {
      return user?.welcome_guide || [];
    }

    return (user?.welcome_guide || []).map((item) => {
      let rentalItem = (rental.welcome_guide || []).find(
        (s) => String(s.parent_id) === String(item.welcome_guide_question_id) &&
          !(resetSections.scope === sectionScopeKey &&
            resetSections.ids.includes(String(s.welcome_guide_question_id))),
      );

      return { ...(rentalItem || item), icon: item.icon, title: item.title };
    });
  }, [user, rental, resetSections, sectionScopeKey]);

  const [mergedSections, setMergedSections] = useState(serverSections);
  const savedSectionOverridesRef = useRef([]);
  const savedSectionScopeRef = useRef(sectionScopeKey);

  useEffect(() => {
    if (savedSectionScopeRef.current !== sectionScopeKey) {
      savedSectionScopeRef.current = sectionScopeKey;
      savedSectionOverridesRef.current = [];
    }

    setMergedSections(
      mergeGuideSectionOverrides(
        serverSections,
        savedSectionOverridesRef.current,
      ),
    );
  }, [sectionScopeKey, serverSections]);

  const handleSectionSaved = useCallback((section, nextOrders) => {
    savedSectionOverridesRef.current = upsertGuideSection(
      savedSectionOverridesRef.current,
      section,
    );
    setMergedSections((current) => upsertGuideSection(current, section));
    if (Array.isArray(nextOrders)) {
      setMergedWelcomeOrder(normalizeWelcomeOrder(nextOrders));
    }
  }, [normalizeWelcomeOrder]);

  const handleSectionReset = useCallback((section, rentalId) => {
    if (savedSectionScopeRef.current !== String(rentalId)) return;
    setResetSections((current) => ({
      scope: String(rentalId),
      ids: [...(current.scope === String(rentalId) ? current.ids : []),
        String(section.welcome_guide_question_id)],
    }));
    savedSectionOverridesRef.current = removeGuideSection(
      savedSectionOverridesRef.current,
      section.welcome_guide_question_id,
    );
    const masterSection = (user?.welcome_guide || []).find(
      (item) => String(item.welcome_guide_question_id) === String(section.parent_id),
    );
    setMergedSections((current) => current.flatMap((item) =>
      String(item.welcome_guide_question_id) === String(section.welcome_guide_question_id)
        ? masterSection ? [masterSection] : []
        : [item],
    ));
    onSaved?.();
  }, [user, onSaved]);

  let isMaster = useMemo(() => {
    return !rental || !rental.rental_id;
  }, [rental]);
  const canManageMasterGuide = canEdit("guides.master");
  const canManageHomeGuide = canEdit("guides.home");
  const canManageCurrentGuide = isMaster
    ? canManageMasterGuide
    : canManageHomeGuide;

  useEffect(() => {
    const loadGuide = async () => {
      document.body.style.overflow = "unset";
      setSelectedTab(null);

      await Promise.all([
        get(["guestbook", "welcome_order", "welcome_tabs", "tags"]),
        canView("messaging.surveys") ? getSurveyList() : Promise.resolve(),
      ]);

      let property_id = window.location.pathname.split("/")[2];
      if (!!property_id && /[0-9]{16}/.test(property_id)) {
        if (!embedded) {
          document.title = "Home Guides";
        }
        await getRental(property_id);
      } else {
        if (!embedded) {
          document.title = "Master Home Guide - RueBaRue";
        }
        setRental({});
      }

      setLoading(false);
    };
    loadGuide();
  }, []);

  useEffect(() => {
    if (typeof onSaved === "function" && !loading) {
      onSaved();
    }
  }, [loading, onSaved, settings, rental, selectedTab]);

  useEffect(() => {
    if (
      selectedTab === null &&
      !!settings?.guestbook &&
      !!settings?.welcome_tabs
    ) {
      const preferredTabType = sessionStorage.getItem(
        MASTER_GUIDE_SELECTED_TAB_KEY,
      );
      const firstTabType =
        (settings?.welcome_tabs || []).find((t) => t.type === "arrival")?.type ||
        settings?.guestbook.tab_order.split(",")[0];
      const selectedTabType = getInitialMasterGuideTabType({
        embedded,
        focusedTabType,
        preferredTabType,
        firstTabType,
      });
      const tab = (settings?.welcome_tabs || []).find(
        (item) => item.type === selectedTabType,
      ) || (settings?.welcome_tabs || []).find(
        (item) => item.type === firstTabType,
      );

      setSelectedTab(tab);
    }
  }, [embedded, focusedTabType, settings, setSelectedTab]);

  useEffect(() => {
    if (!focusedTabType || !settings?.welcome_tabs) return;
    const focusedTab = settings.welcome_tabs.find(
      (tab) => tab.type === focusedTabType,
    );
    if (focusedTab && selectedTab?.type !== focusedTabType) {
      setSelectedTab(focusedTab);
    }
  }, [focusedTabType, selectedTab?.type, setSelectedTab, settings?.welcome_tabs]);

  useEffect(() => {
    if (!selectedTab?.type) {
      return;
    }

    sessionStorage.setItem(MASTER_GUIDE_SELECTED_TAB_KEY, selectedTab.type);

    requestAnimationFrame(() => {
      document
        .querySelector(`[data-tab-type="${selectedTab.type}"]`)
        ?.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
  }, [selectedTab]);

  const getImages = useCallback(
    (guide) =>
      (guide.attachments || []).filter((attachment) => attachment.type === "image" && attachment.url),
    [],
  );

  const getPdf = useCallback(
    (guide) =>
      guide.attachments?.find(
        (attachment) => attachment.type === "file" && !!attachment.url,
      ),
    [],
  );

  const getVideo = useCallback((item) => {
    let video = item.attachments?.find(
      (attachment) => attachment.type === "you_tube"
    );

    if (!video) {
      return null;
    }

    let youtubeRegex =
        /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w\-]{11})/,
      vimeoRegex =
        /(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|video\/|)(\d+)(?:\/(\w+))?/,
      loomRegex =
        /^https:\/\/(www\.)?loom\.com\/share\/([\w\d]+)(\?sid=[\w\d-]+)?$/;

    const url = video.url || "";

    const youtubeMatch = url.match(youtubeRegex);
    const vimeoMatch = url.match(vimeoRegex);
    const loomMatch = url.match(loomRegex);

    if (youtubeMatch && youtubeMatch[1].length === 11) {
      const videoId = youtubeMatch[1];
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    } else if (vimeoMatch) {
      const videoId = vimeoMatch[4];
      const hash = vimeoMatch[5];
      let iframeSrc = `https://player.vimeo.com/video/${videoId}`;
      if (hash) iframeSrc += `?h=${hash}`;
      return iframeSrc;
    } else if (loomMatch && loomMatch[2]) {
      const videoId = loomMatch[2];
      const hash = loomMatch[2];
      let iframeSrc = `https://www.loom.com/embed/${videoId}`;
      if (hash) iframeSrc += `?h=${hash}`;
      return iframeSrc;
    } else {
      return null;
    }
  }, []);
  
  const alterAssetUrl = (url) => {
    return url
      .replace("http://uploads.ruebarue.com", "https://public.ruebarue.com")
      .replace(
        "s3.us-east-2.amazonaws.com/uploads.ruebarue.com",
        "public.ruebarue.com",
      );
  };

  const handleEdit = useCallback((section) => {
    setSelectedSection(section);
  }, []);

  const handleTitleEdit = () => {
    setisEditTitle(true);
  };

  const handleBack = () => {
    setSelectedTab(null);
    setRental({});
    history.push("/properties");
  };

  const handleSaveTitle = async () => {
    let result = await updateRental(rental.rental_id, {
      ...rental,
      name: title,
    });
    if (result) {
      getRental(rental.rental_id);
    }
    setisEditTitle(false);
  };

  const handleDelete = useCallback((section) => {
    setSelectedSection(section);
  }, []);

  const handleExpand = useCallback(
    async (section) => {
      const nextSection = { ...section, expanded: !section.expanded };
      try {
        const [saved, error] = await updateSection(nextSection);
        if (error || !saved) throw new Error("Block save was not confirmed");
        handleSectionSaved(mergeSavedGuideSection(section, nextSection, saved));
        setPreviewExpandedSections((current) => {
          const next = { ...current };
          delete next[`${sectionScopeKey}:${section.welcome_guide_question_id}`];
          return next;
        });
      } catch {
        UIkit.notification("Unable to save this block. Please try again.", "error");
        return;
      }

      try {
        await Promise.all([
          refresh(),
          rental?.rental_id ? getRental(rental.rental_id) : Promise.resolve(),
        ]);
      } catch {
        UIkit.notification("Block saved, but the guide could not refresh. Please reload.", "warning");
      }
    },
    [rental, sectionScopeKey, updateSection, handleSectionSaved, getRental, refresh],
  );

  const handleVisible = useCallback(
    async (section, event) => {
      setProcessing(true);
      setSelectedSection(section);
      const nextSection = { ...section, suspended: !event.target.checked };
      let savedSection;
      try {
        let saveFn = updateSection;
        if (+section.parent_id === 0 || +section.rental_id === 0) {
          nextSection.parent_id = section.welcome_guide_question_id;
          nextSection.rental_id = rental.rental_id;
          saveFn = createSection;
        }
        const [saved, error] = await saveFn(nextSection);
        if (error || !saved) throw new Error("Block save was not confirmed");
        savedSection = mergeSavedGuideSection(section, nextSection, saved);
        handleSectionSaved(savedSection);
        setSelectedSection((current) =>
          String(current?.welcome_guide_question_id) === String(section.welcome_guide_question_id)
            ? savedSection
            : current,
        );
      } catch {
        UIkit.notification("Unable to save this block. Please try again.", "error");
      } finally {
        setProcessing(false);
      }
      if (!savedSection) return;

      try {
        await Promise.all([
          refresh(),
          rental?.rental_id ? getRental(rental.rental_id) : Promise.resolve(),
        ]);
      } catch {
        UIkit.notification("Block saved, but the guide could not refresh. Please reload.", "warning");
      }
    },
    [rental, updateSection, createSection, handleSectionSaved, setSelectedSection, getRental, refresh],
  );

  const [reordering, setReordering] = useState(false);
  const handleReorder = async ({ oldIndex, newIndex }) => {
    if (!isMaster || reordering || oldIndex < 0 || newIndex < 0 || newIndex >= sectionOrder.length) return;
    const ordering = arrayMoveImmutable(sectionOrder, oldIndex, newIndex);
    const nextWelcomeOrder = normalizeWelcomeOrder(settings?.welcome_order || [])
      .map((order) => order.type === selectedTab.type
        ? { ...order, ordering: normalizeOrdering(ordering) } : order);
    setReordering(true);
    try {
      await set("welcome_order", nextWelcomeOrder);
      setSettings((previous) => ({ ...previous, welcome_order: nextWelcomeOrder }));
      setMergedWelcomeOrder(nextWelcomeOrder);
    } catch {
      UIkit.notification("Unable to save block order. Please try again.", "error");
    } finally {
      setReordering(false);
    }
  };

  const handleIcon = useCallback((section) => {
    setSelectedSection(section);
  }, []);

  const SortableItemDiv = SortableElement(function GuideBlockPreview({ section }) {
    const blockId = section.welcome_guide_question_id;
    const previewKey = `${sectionScopeKey}:${blockId}`;
    const isExpanded = previewExpandedSections[previewKey] ?? !!section.expanded;
    const { contentRef, overflows } = usePreviewOverflow(embedded);
    const showViewToggle = embedded && overflows;

    return (
      <div
        className={classnames(
          "uk-card uk-card-small uk-card-default hms-property-card",
          {
            "is-collapsed": showViewToggle && !isExpanded,
            "is-expanded": showViewToggle && isExpanded,
          },
        )}
        style={{
          backgroundColor:
            !isMaster && +section.rental_id !== 0 ? "var(--light-red)" : "",
        }}
        >
        <div className="uk-flex uk-flex-between">
          <div className="hms-property-card-body">
            <div className="uk-flex">
              <div className="m-w-38">
                <GuideIcon
                  icon={section?.icon == null ? "info" : section.icon}
                  uk-toggle={isMaster && canManageMasterGuide ? "target: #iconList-modal" : ""}
                  onClick={isMaster && canManageMasterGuide ? () => handleIcon(section) : undefined}
                ></GuideIcon>
              </div>
              <div className="hms-property-card-content">
                <h3>{section.title}</h3>
                {isMaster && (
                  <div className="hms-guest-tags">
                    <div className="guest-tags">
                      {section.tags
                        ?.filter((t) => !!t)
                        ?.sort((prev, next) => prev.name.localeCompare(next.name))
                        ?.map((tag, index) => (
                          <div className="uk-inline" key={index}>
                            {tag.name}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                <div
                  className={classnames("hms-property-card-preview", {
                    "is-collapsed": showViewToggle && !isExpanded,
                    "is-expanded": showViewToggle && isExpanded,
                  })}
                >
                  <div ref={contentRef} className="hms-property-card-preview-content">
                  <div>
                    <Markdown input={section.body} />
                  </div>
                  {!!getPdf(section) && (
                    <div className="hsm-pdf uk-margin-top">
                      <Image
                        src="/images/pdf-icon.svg"
                        className="uk-margin-small-right"
                      />
                      <Link
                        target="_blank"
                        href={alterAssetUrl(getPdf(section).url)}
                      >
                        {getPdf(section).name || getPdf(section).original_name}
                      </Link>
                    </div>
                  )}
                  {getImages(section).length > 0 && (
                    <GuidePhotoPreview
                      photos={getImages(section).map((photo) => ({ ...photo, url: alterAssetUrl(photo.url) }))}
                      title={section.title}
                    />
                  )}
                  {!!getVideo(section) && (
                    <div className="video-responsive uk-margin-top">
                      <iframe
                        width="585"
                        height="330"
                        src={getVideo(section)}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        title="Embedded youtube"
                      />
                    </div>
                  )}
                  {!!section.restrictions && isMaster && (
                    <div className="extras-settings">
                      <ul class="uk-list">
                        <VisibilityShow restrictions={section.restrictions} />
                      </ul>
                    </div>
                  )}
                  </div>
                </div>
                {showViewToggle && (
                  <div className="hms-property-card-preview-actions">
                    <button
                      type="button"
                      className="uk-button hms-property-card-toggle"
                      onClick={() => setPreviewExpandedSections((current) => ({
                        ...current,
                        [previewKey]: !isExpanded,
                      }))}
                    >
                      <span>{isExpanded ? "View less" : "View more"}</span>
                      <FeatherIcon
                        name={isExpanded ? "chevron-up" : "chevron-down"}
                      />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="hms-property-card-btn guide-actions">
            {canManageCurrentGuide && (
              <div>
                <button
                  className="uk-button"
                  uk-toggle={`target: #${MASTER_PROPERTY_GUIDE_EDIT_MODAL_NAME}`}
                  uk-tooltip="Edit Block"
                  onClick={() => handleEdit(section)}
                >
                  <FeatherIcon name="edit-3" />
                </button>
              </div>
            )}{" "}
            {isMaster && canManageMasterGuide && (
              <>
                <hr />
                <ReorderControls id={`block-${blockId}`} noun="block"
                  index={sectionOrder.findIndex((id) => String(id) === String(blockId))}
                  count={sectionOrder.length} disabled={reordering}
                  onMove={(oldIndex, newIndex) => handleReorder({ oldIndex, newIndex })} />
                <hr />
                <div className={section.expanded ? "active" : ""}>
                  <button
                    className={`uk-button`}
                    uk-tooltip={!section.expanded ? "Expand Block" : "Collapse Block"}
                    onClick={() => handleExpand(section)}
                  >
                    <FeatherIcon
                      name={section.expanded ? "minimize-2" : "maximize-2"}
                    />
                  </button>
                </div>{" "}
                <hr />
                <div>
                  <button
                    className="uk-button"
                    onClick={() => handleDelete(section)}
                    uk-toggle="target: #master-property-delete"
                    uk-tooltip="Delete Block"
                  >
                    <FeatherIcon name="trash-2" />
                  </button>
                </div>
              </>
            )}
            {!isMaster && canManageCurrentGuide && +section.rental_id !== 0 && (
              <div>
                <hr />
                <div>
                  <button
                    className="uk-button"
                    onClick={() => handleDelete(section)}
                    uk-toggle="target: #reset-property"
                    uk-tooltip="Reset to Master"
                  >
                    <Image src="/images/reset-icon.svg" />
                  </button>
                </div>
              </div>
            )}
            {!isMaster && (
              <div>
                <hr />
                <div>
                  <button
                    className="uk-button"
                    uk-tooltip="Open Block"
                    onClick={() =>
                      window.open(
                        `https://guide.ruebarue.com/block/${rental.rental_id}/${blockId}`,
                        "_blank"
                      )
                    }
                  >
                    <Image src="/images/link-icon.svg" />
                  </button>
                </div>{" "}              
                <hr />
                <div>
                  <QRPart
                    idValue={`block-${rental.rental_id}-${blockId}`}
                    qrUrl={`https://guide.ruebarue.com/block/${rental.rental_id}/${blockId}`}
                    iconSrc="/images/qr-icon.svg"
                    tooltip="Download Block QR Code"
                  />
                </div>{" "}
                <hr />
                {canManageCurrentGuide && (
                  <div
                    className="autosend-checkbox uk-margin-top uk-margin-small-bottom"
                    uk-tooltip={section.suspended ? "Show Block" : "Hide Block"}
                  >
                    {isProcessing && selectedSection === section ? (
                      <div data-uk-spinner="ratio: 0.5;"></div>
                    ) : (
                      <label className="setting-switch">
                        <input
                          type="checkbox"
                          onChange={(event) => handleVisible(section, event)}
                          checked={!section.suspended}
                        />
                        <span className="setting-slider setting-round"></span>
                      </label>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  });

  const SortableList = SortableContainer(({ sections }) => {
    return (
      <div className="hms-master-guide-content">
        {sections?.map((section, index) => {
          if (!isMaster && section.tags?.length > 0) {
            return section.tags?.some((tag) => {
              return rental?.tags?.find((item) => item.name === tag.name);
            }) ? (
              <SortableItemDiv
                key={section.welcome_guide_question_id}
                index={index}
                section={section}
              />
            ) : (
              <></>
            );
          }
          return (
            <SortableItemDiv
              key={section.welcome_guide_question_id}
              index={index}
              section={section}
            />
          );
        })}
      </div>
    );
  });

  const handleDeleteTab = async () => {
    try {
      await nameSaveQueue.flush();
    } catch {
      UIkit.notification("Save the section name before continuing.", "error");
      return;
    }
    const settings = settingsRef.current;
    const { guestbook, welcome_tabs, welcome_order } = settings;
    if (!isMaster || !selectedTab || !guestbook) {
      return;
    }

    const deletedTabType = selectedTab.type;
    const updatedSettings = removeMasterGuideTab(settings, deletedTabType);
    const nextTabType = updatedSettings.guestbook.tab_order.split(",")[0];
    const nextTab =
      updatedSettings.welcome_tabs.find((tab) => tab.type === nextTabType) ||
      null;

    setSettings((previous) => ({ ...previous, ...updatedSettings }));
    sessionStorage.removeItem(MASTER_GUIDE_SELECTED_TAB_KEY);
    setSelectedTab(nextTab);

    try {
      const savedSettings = await set(
        ["guestbook", "welcome_tabs", "welcome_order"],
        updatedSettings,
      );

      if (!savedSettings) {
        throw new Error("Unable to save master guide tab settings");
      }

      UIkit.notification("Tab Deleted Successfully", "success");
      UIkit.modal("#master-tab-delete").hide();
    } catch (err) {
      setSettings((previous) => ({
        ...previous,
        guestbook,
        welcome_tabs,
        welcome_order,
      }));
      setSelectedTab(selectedTab);
      UIkit.notification("Failed", "error");
    }
  };

  const handleEditTab = (tab) => {
    setSelectedTab(welcome_tabs.find((t) => t.type === tab.type));
  };

  const handleOrderTab = async ({ oldIndex, newIndex }, e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const displayedTabOrder = orderedTabs.map((tab) => tab.type);
    const move = getMasterGuideTabMove(
      displayedTabOrder,
      oldIndex,
      newIndex,
    );
    if (!move) {
      return;
    }

    const reorderedTabs = arrayMoveImmutable(
      displayedTabOrder,
      move.oldIndex,
      move.newIndex,
    );
    const result = { ...guestbook, tab_order: reorderedTabs.join(",") };
    setSettings((previous) => ({ ...previous, guestbook: result }));

    try {
      const savedGuestbook = await setGuestbookTabOrder(reorderedTabs);
      if (savedGuestbook?.tab_order !== result.tab_order) {
        throw new Error("Saved tab order does not match requested order");
      }
    } catch (err) {
      setSettings((previous) => ({ ...previous, guestbook }));
      UIkit.notification("Failed", "error");
    }
  };

  const handleAddSection = async () => {
    try {
      await nameSaveQueue.flush();
    } catch {
      UIkit.notification("Save the section name before continuing.", "error");
      return;
    }
    const settings = settingsRef.current;
    const { guestbook, welcome_tabs, welcome_order } = settings;
    const label = (newSectionName || "").trim();

    if (!isMaster || !label || !settings?.welcome_tabs || !settings?.welcome_order || !guestbook) {
      return;
    }

    const newTabId = Math.round(Math.random() * Math.pow(10, 16)).toString();

    const nextTabs = [
      ...settings.welcome_tabs,
      {
        type: newTabId,
        label,
        show_on_guide: true,
        show_on_embed: true,
      },
    ];

    const nextOrder = [
      ...settings.welcome_order,
      {
        type: newTabId,
        ordering: [],
      },
    ];

    const nextGuestbook = {
      ...guestbook,
      tab_order: [...(guestbook.tab_order || "").split(",").filter(Boolean), newTabId].join(","),
    };

    const nextSettings = {
      welcome_tabs: nextTabs,
      welcome_order: nextOrder,
      guestbook: nextGuestbook,
    };
    setSettings((previous) => ({ ...previous, ...nextSettings }));
    setNewSectionName("");
    setSelectedTab(nextTabs.find((tab) => tab.type === newTabId) || null);

    try {
      await set(["welcome_tabs", "welcome_order", "guestbook"], nextSettings);
      UIkit.notification("Added Successfully", "success");
      onSaved?.();
    } catch (error) {
      setSettings((previous) => ({
        ...previous,
        welcome_tabs,
        welcome_order,
        guestbook,
      }));
      setSelectedTab(selectedTab);
      setNewSectionName(label);
      UIkit.notification("Failed", "error");
    }
  };


  return (
    <>
      {loading == true ? (
        <Loading />
      ) : (
        <section
          id={embedded ? undefined : "master-property-template"}
          className={embedded ? "guide-designer-master-home-guide" : undefined}
        >
          {!embedded && (
            <div id="hms-page-title">
              <div className="uk-flex uk-flex-between uk-flex-middle">
                {isMaster ? (
                  <h1 className="uk-heading-small">Master Home Guide</h1>
                ) : (
                  <>
                    <div className="uk-flex uk-flex-middle">
                      <button
                        className="uk-button hms-img-btn go-back-arrow-btn"
                        onClick={handleBack}
                      >
                        <span uk-icon="icon: arrow-left; ratio: 1"></span>
                      </button>
                      {isEditTitle ? (
                        <div>
                          <input
                            type="text"
                            value={title}
                            style={{ width: "400px" }}
                            onChange={(e) => setTitle(e.target.value)}
                          />
                          <span onClick={handleSaveTitle}>
                            <Icon options="check" button={true} />
                          </span>
                          <span onClick={() => setisEditTitle(false)}>
                            <Icon options="close" button={true} />
                          </span>
                        </div>
                      ) : (
                        <>
                          <h1 className="uk-heading-small">{title}</h1>
                          {canManageCurrentGuide && (
                            <button
                              className="uk-button hms-simple-icon-btn"
                              onClick={handleTitleEdit}
                            >
                              <FeatherIcon name="edit-3" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    <div className="hms-page-title-btn uk-flex uk-flex-middle uk-flex-between">
                      {canManageCurrentGuide && (
                        <button
                          className="uk-button hms-btn hms-green-btn uk-margin-small-right"
                          onClick={() => setRental(rental)}
                          uk-toggle="target: #share-modal"
                          uk-tooltip="Share"
                        >
                          Share
                        </button>
                      )}
                      <button className="uk-button hms-btn hms-blue-btn">
                        <a
                          href={`https://guide.ruebarue.com/rental/${rental.rental_id}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "white" }}
                        >
                          Guest View
                        </a>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div
            id={embedded ? undefined : "hms-main-body"}
            className={classnames("hms-master-guide-shell", {
              "is-embedded": embedded,
              "is-onboarding-focused": embedded && !!focusedTabType,
            })}
          >
            <div className="hms-master-guide-layout">
              <aside className="hms-property-tabs" aria-label="Master guide tabs">
                {isMaster && (
                  <div className="guide-designer-embedded-sidebar-header">
                    <div className="guide-designer-embedded-sidebar-title-row">
                      <h3 className="uk-margin-remove-bottom">Home Guide Template</h3>
                      <button
                        type="button"
                        className="uk-button uk-button-text guide-designer-header-link"
                        onClick={() => history.push("/properties")}
                      >
                        Open Home Guides
                      </button>
                    </div>
                    <p className="secondary">
                      Group your home guide content into sections for guests, such as Wi-Fi,
                      arrival details, house rules, or checkout. Changes save automatically
                      and are live in the guestbook.
                    </p>
                  </div>
                )}
                <div className="guide-designer-embedded-sidebar-body">
                  <SortableListTab
                    tabs={orderedTabs}
                    selectedTab={selectedTab}
                    isMaster={isMaster}
                    canManageMasterGuide={canManageMasterGuide}
                    resolveTabLabel={resolveTabLabel}
                    setSelectedTab={setSelectedTab}
                    handleEditTab={handleEditTab}
                    onRename={handleRenameTab}
                    useDragHandle
                    onSortEnd={
                      canManageMasterGuide ? handleOrderTab : undefined
                    }
                    lockAxis="y"
                    pressDelay={100}
                    helperClass="master-guide-sort-helper"
                  />
                  {isMaster && canManageMasterGuide && (
                    <div className="uk-width-1-1 uk-margin-top">
                      <div className="add-dest-gd-tab-wrapper">
                        <div className="uk-flex uk-flex-middle">
                          <input
                            className="uk-input"
                            type="text"
                            placeholder="Section Name"
                            value={newSectionName}
                            onChange={(e) => setNewSectionName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddSection();
                              }
                            }}
                          />
                          <div className="plus-w-2">
                            <button
                              className="uk-button hms-btn hms-gray-btn btn-no-margin"
                              type="button"
                              onClick={handleAddSection}
                            >
                              ADD
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </aside>

              <div className="hms-master-guide-main">
                {isMaster && canManageMasterGuide && (
                  <div className="uk-flex uk-flex-between uk-flex-top">
                    <div className="hms-guest-btn">
                      <button
                        className="uk-button hms-btn hms-red-btn"
                        uk-toggle={`target: #${MASTER_PROPERTY_GUIDE_ADD_MODAL_NAME}`}
                      >
                        <span uk-icon="icon: plus; ratio: 0.7"></span> Block
                      </button>
                    </div>
                  </div>
                )}
                <div
                  className={classnames("hms-master-guide-content-row", {
                    "is-property-guide": !isMaster,
                  })}
                >
                  <SortableList
                    sections={selectOrderedGuideSections(
                      mergedSections,
                      sectionOrder,
                    )}
                    useDragHandle
                    onSortEnd={
                      canManageCurrentGuide ? handleReorder : undefined
                    }
                  />
                  {!isMaster && <HomeSection />}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Share />
      <MasterPropertyGuideAdd
        onSaved={onSaved}
        onSectionSaved={handleSectionSaved}
      />
      <MasterPropertyGuideEdit
        onSaved={onSaved}
        onSectionSaved={handleSectionSaved}
      />
      <TabAdd />
      <IconList onSectionSaved={handleSectionSaved} />
      <MasterDelete />
      <ResetToMaster onSectionReset={handleSectionReset} />
      <MasterTabDelete onDelete={handleDeleteTab} />
    </>
  );
};

export default PropertyGuide;
